import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Grid } from "@/comn/components";
import { comnUtils, comnEnvs } from "@/comn/utils"; // 프로젝트 공통 유틸
import { Page, Group, Layout, Button, FormControl } from "@/comn/components"; // 화면 구성 컴포넌트
import { useFetch, useForm, useGrid, useStore, useToast, useResource, useModal, usePage } from "@/comn/hooks";
import {
    BASE,
    URLS,
    APIS,
    SF_BTCH_INFO_SEARCH_FORM,
    SG_BTCH_INFO_LIST,
} from "@/tra/tancis/ptli/btch/info/service/PtliBtchInfoMgmtService";

/*
 * Manage batch information list
 * !== 배치정보관리 목록 ==!
 */
export const PTLI1201001Q = (props: any) => {
    /*
     * Declaration Hook, Meta
     * !== Hook, 메타 정보 정의 ==!
     */
    const { pgeUid, menuId } = usePage({
        auth: ["auth_ptli0015", "auth_ptlianyADM", "auth_ptli0047", "auth_ptli0048"],
    });
    const { t } = useTranslation(); // Translation Hook !== 언어 변환 Hook ==!
    const navigate = useNavigate(); // Navigate Hook !== 화면 이동 Hook ==!
    const modal = useModal();
    const toast = useToast(); // Toast Message Hook !== Toast 메세지 표시 Hook ==!
    const { pgeStore, setStore } = useStore({ pgeUid: pgeUid }); // Page Store Hook !== 화면 데이터 저장 Hook ==!

    useResource({
        defaultSchema: [{ area: "comnCd", comnCd: "COM0024" }],
    });

    /*
     * Declaration Form
     * !== 폼 정의 ==!
     */
    const form = {
        // Manage batch information Form !== 배치정보관리 Form ==!
        btchInfoSrch: useForm({
            defaultSchema: SF_BTCH_INFO_SEARCH_FORM,
            defaultValues: {
                kwdSel: null,
                ...(pgeStore?.form || {}),
            },
        }),
    };

    /*
     * Declaration Grid
     * !== 그리드 정의 ==!
     */
    const grid = {
        // Batch List !== 알림 목록 ==!
        btchInfoSrchList: useGrid({
            defaultSchema: SG_BTCH_INFO_LIST,
            page: pgeStore?.page,
            size: pgeStore?.size,
        }),
    };

    /*
     * Declaration Fetch
     * !== Fetch 정의 ==!
     */
    const fetch = {
        // Get a list of batch information. !== 배치정보 목록을 조회한다. ==!
        getBtchInfoList: useFetch({
            api: (page = grid.btchInfoSrchList.page) =>
                APIS.getBtchInfoList(form.btchInfoSrch.getValues(), page, grid.btchInfoSrchList.size),
            enabled: comnUtils.isEmpty(form.btchInfoSrch.errors),
            key: [grid.btchInfoSrchList.page, grid.btchInfoSrchList.size],
            onSuccess: () => {
                setStore(pgeUid, {
                    form: form.btchInfoSrch.getValues(),
                    page: grid.btchInfoSrchList.page,
                    size: grid.btchInfoSrchList.size,
                });
            },
            onError: () => toast.showToast({ type: "error", content: "msg.com.00025" }), // Failed to select. !== 조회가 실패하였습니다. ==!
            showToast: true,
        }),
    };

    /*
     * Declaration Event Render
     * !== 랜더 정의 ==!
     */
    const render = {
        grid_btchInfoSrchList: {
            cell: {
                btchNm: (props: any) => {
                    const { rowValues, value } = props;
                    return <Link to={`/ptli/btch/info/ptli1201004q/${rowValues.btchId}`}>{value}</Link>;
                },
                onlnExcutnPsblYn: (props: any) => {
                    const { value } = props;
                    return value === "Y" ? "Yes" : "No";
                },
            },
        },
    };

    /*
     * Declaration Event Handler
     * !== 이벤트 핸들러 정의 ==!
     */
    const handler = {
        // Click Search Button !== 검색버튼 ==!
        getBtchInfoList: (page: number = 0) => {
            form.btchInfoSrch.handleSubmit(
                () => {
                    grid.btchInfoSrchList.setPage(page);
                    fetch.getBtchInfoList.fetch(page);
                },
                () => {
                    toast.showToast({ type: "warning", content: "msg.00002" });
                },
            )();
        },
        // Click Registration Button !== 등록 버튼 클릭 ==!
        onNewBtnClick: () => navigate(URLS.ptli1201002s),
        // Enter Keyword text !== 검색어 엔터 이벤트 ==!
        onEnterKwdTxt: () => {
            grid.btchInfoSrchList.setPage(0);
            fetch.getBtchInfoList.fetch(0);
        },
    };

    /*
     * Declaration Page Init Function
     * !== 화면 초기화 함수 선언  ==!
     */
    useEffect(() => {
        handler.getBtchInfoList();
    }, []);

    const kwdSel = form.btchInfoSrch.watch("kwdSel");

    return (
        <Page>
            <Group>
                <Group.Body>
                    <Group.Section>
                        <Group.Row>
                            <Group.Control {...form.btchInfoSrch.schema.bsopClsfCd} />
                            <Group.Label label={"L_KWD"} />
                            <Group.Col padding={1}>
                                <Layout direction="row" gap={1}>
                                    <FormControl {...form.btchInfoSrch.schema.kwdSel} />
                                    <FormControl
                                        {...form.btchInfoSrch.schema.kwdTxt}
                                        readOnly={!kwdSel}
                                        onEnter={handler.onEnterKwdTxt}
                                    />
                                </Layout>
                            </Group.Col>
                        </Group.Row>
                    </Group.Section>

                    <Layout direction="row">
                        <Layout.Left>
                            <Button role={"reset"} onClick={() => form.btchInfoSrch.reset()} />
                        </Layout.Left>
                        <Layout.Right>
                            <Button
                                role="search"
                                onClick={() => {
                                    handler.getBtchInfoList();
                                }}
                            ></Button>
                        </Layout.Right>
                    </Layout>
                </Group.Body>
            </Group>

            <Group>
                <Group.Body>
                    <Group.Section>
                        <Grid
                            {...grid.btchInfoSrchList.grid}
                            data={fetch.getBtchInfoList.data?.btchInfoList}
                            render={render.grid_btchInfoSrchList}
                        />
                    </Group.Section>
                </Group.Body>
            </Group>

            <Layout direction="row">
                <Layout.Left>
                    <Button role={"new"} onClick={handler.onNewBtnClick} />
                </Layout.Left>
            </Layout>
        </Page>
    );
};
